import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import "./Dashbot.css";
import { useLoginProvider } from "../../context/LoginContext";

/* Helpers */
const parseDate = (s) => {
  if (!s) return null;
  // Accept "YYYY-MM-DD" or ISO
  const d = s.length === 10 ? new Date(`${s}T00:00:00`) : new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (s) => {
  const d = parseDate(s);
  if (!d) return "—";
  // 05 Aug 2025
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatMoney = (n, currency = "USD") => {
  const amt = Number(n || 0);
  try {
    return amt.toLocaleString("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
  } catch {
    // fallback if currency code not supported
    return `$${amt.toFixed(2)}`;
  }
};


const byLatest = (a, b) => {
  const ad = parseDate(a.transactionDate)?.getTime() ?? 0;
  const bd = parseDate(b.transactionDate)?.getTime() ?? 0;
  return bd - ad;
};

export default function DashboardBot() {
  const { user } = useLoginProvider();
  const [allTxns, setAllTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | credited | withdraw

  const userId = user?.uid;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!userId) return;
        const ref = doc(db, "users", userId);
        const snap = await getDoc(ref);
        if (!snap.exists()) return;

        const data = snap.data() || {};
        let txns = data.bankDetails?.transactions || [];
        // Normalize + sort
        txns = (txns || []).map((t) => ({
          transactionDate: t.transactionDate || "",
          transactionId: t.transactionId || "",
          amount: Number(t.amount ?? 0),
          mode: t.mode || "—",
          type: (t.type || "").toLowerCase() === "withdraw" ? "withdraw" : "credited",
          currency: t.currency || data.bankDetails?.currency || "USD",
        }));
        txns.sort(byLatest);

        if (mounted) setAllTxns(txns);
      } catch (e) {
        console.error("Error fetching transactions:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTxns.filter((t) => {
      const okType = typeFilter === "all" ? true : t.type === typeFilter;
      const okQuery =
        !q ||
        t.transactionId.toLowerCase().includes(q) ||
        t.mode.toLowerCase().includes(q);
      return okType && okQuery;
    });
  }, [allTxns, query, typeFilter]);

  const totals = useMemo(() => {
    let credited = 0;
    let withdraw = 0;
    filtered.forEach((t) => {
      if (t.type === "credited") credited += Number(t.amount || 0);
      else withdraw += Number(t.amount || 0);
    });
const currency = filtered[0]?.currency || "USD";

    return {
      creditedText: formatMoney(credited, currency),
      withdrawText: formatMoney(withdraw, currency),
      netText: formatMoney(credited - withdraw, currency),
      currency,
    };
  }, [filtered]);

  if (loading) {
    return (
      <div className="dashboardbot glass-card">
        <div className="dbot-head">
          <h2>Transaction History</h2>
        </div>
        <div className="table-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="row-skel" key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboardbot glass-card">
      <div className="dbot-head">
        <h2>Transaction History</h2>

        <div className="controls">
          <div className="segmented">
            <button
              className={typeFilter === "all" ? "on" : ""}
              onClick={() => setTypeFilter("all")}
              aria-pressed={typeFilter === "all"}
            >
              All
            </button>
            <button
              className={typeFilter === "credited" ? "on" : ""}
              onClick={() => setTypeFilter("credited")}
              aria-pressed={typeFilter === "credited"}
            >
              Credited
            </button>
            <button
              className={typeFilter === "withdraw" ? "on" : ""}
              onClick={() => setTypeFilter("withdraw")}
              aria-pressed={typeFilter === "withdraw"}
            >
              Withdraw
            </button>
          </div>

          <div className="search">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by ID or mode…"
              aria-label="Search transactions"
            />
            <span className="kbd">/</span>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty">
          <div className="emoji">🧾</div>
          <p>No transactions found.</p>
          <span className="hint">Try clearing filters or search.</span>
        </div>
      ) : (
        <div className="transaction-table-wrapper">
          <table className="transaction-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction ID</th>
                <th>Mode</th>
                <th className="align-right">Amount</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, idx) => (
                <tr key={`${t.transactionId}-${idx}`} className="row">
                  <td data-label="Date">{formatDate(t.transactionDate)}</td>
                  <td data-label="ID" className="mono">{t.transactionId || "—"}</td>
                  <td data-label="Mode">
                    <span className="chip chip-mode">{t.mode}</span>
                  </td>
                  <td data-label="Amount" className="align-right mono amount">
                    {t.type === "withdraw" ? "−" : "+"}
                    {formatMoney(Math.abs(t.amount), t.currency)}
                  </td>
                  <td data-label="Type">
                    <span className={`chip ${t.type === "withdraw" ? "chip-red" : "chip-green"}`}>
                      {t.type === "withdraw" ? "Withdrawn" : "Credited"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="3" className="tfoot-label">Totals</td>
                <td className="align-right mono">
                  <div className="totals">
                    <div><span className="tag t-green">Credited</span> {totals.creditedText}</div>
                    <div><span className="tag t-red">Withdraw</span> {totals.withdrawText}</div>
                    <div><span className="tag t-net">Net</span> {totals.netText}</div>
                  </div>
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
